import React, { useState, useEffect } from "react";
import API_CLIENT from "../../lib/api-client";
import { GET_ALL_PRODUCTS, GET_PRODUCT_BY_ID } from "../../utils/constant";
import { faHeart, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faCartShopping, faFire, faEye } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { ProductCardSkeleton } from "../../component/Skeleton";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";
import { toast } from "react-hot-toast";
import { useCartStore } from "../../store/slice/cartSlice";
import { useWishlistStore } from "../../store/slice/wishlistSlice";
const ProductPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: "All",
    manufacturer: "All",
    state: "All",
    minPrice: "",
    maxPrice: "",
    search: "",
  });
  const [sortBy, setSortBy] = useState("default");
  const [error, setError] = useState(null);
  const isOnline = useNetworkStatus();

  useEffect(() => {
    if (!isOnline) {
      toast.error("No internet connection. Please check your network.");
      return;
    }

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await API_CLIENT.get(GET_ALL_PRODUCTS, {
          withCredentials: true,
        });
        if (response.status === 200) {
          setProducts(response.data.products);
        }
      } catch (error) {
        console.error("Error details:", error);

        setError(
          error.response?.data?.message ||
          "Unable to load products. Please check your connection and try again."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [isOnline]);

  // Get unique values for filters
  const categories = [...new Set(products.map((p) => p.category.name))];
  const manufacturers = [...new Set(products.map((p) => p.manufacturer))];
  const states = [...new Set(products.map((p) => p.state))];

  // Filter and Sort products
  const filteredProducts = products
    .filter((product) => {
      if (
        filters.category !== "All" &&
        product.category.name !== filters.category
      )
        return false;
      if (
        filters.manufacturer !== "All" &&
        product.manufacturer !== filters.manufacturer
      )
        return false;
      if (filters.state !== "All" && product.state !== filters.state)
        return false;
      if (filters.minPrice && product.price < Number(filters.minPrice))
        return false;
      if (filters.maxPrice && product.price > Number(filters.maxPrice))
        return false;
      if (
        filters.search &&
        !product.name.toLowerCase().includes(filters.search.toLowerCase())
      )
        return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "priceAsc":
          return a.price - b.price;
        case "priceDesc":
          return b.price - a.price;
        case "newest":
          return new Date(b.createdAt) - new Date(a.createdAt);
        case "popular":
          return b.saleCount - a.saleCount;
        default:
          return 0;
      }
    });

  // Show offline message
  if (!isOnline) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-red-50 p-6 rounded-lg text-center">
          <h2 className="text-red-800 text-xl font-semibold mb-2">
            You're offline
          </h2>
          <p className="text-red-600">
            Please check your internet connection and try again
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen flex">
      {/* Filters Sidebar */}
      <div className="w-64 bg-white shadow-lg p-6 hidden lg:block">
        <h2 className="text-xl font-bold mb-6">Filters</h2>

        {/* Category Filter */}
        <div className="mb-6">
          <label className="block font-semibold text-gray-700 mb-2">
            Category
          </label>
          <select
            value={filters.category}
            onChange={(e) =>
              setFilters({ ...filters, category: e.target.value })
            }
            className="w-full p-2 border rounded-md"
          >
            <option value="All">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Manufacturer Filter */}
        <div className="mb-6">
          <label className="block font-semibold text-gray-700 mb-2">
            Manufacturer
          </label>
          <select
            value={filters.manufacturer}
            onChange={(e) =>
              setFilters({ ...filters, manufacturer: e.target.value })
            }
            className="w-full p-2 border rounded-md"
          >
            <option value="All">All Manufacturers</option>
            {manufacturers.map((mfr) => (
              <option key={mfr} value={mfr}>
                {mfr}
              </option>
            ))}
          </select>
        </div>

        {/* Price Range */}
        <div className="mb-6">
          <label className="block font-semibold text-gray-700 mb-2">
            Price Range
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Min"
              value={filters.minPrice}
              onChange={(e) =>
                setFilters({ ...filters, minPrice: e.target.value })
              }
              className="w-1/2 p-2 border rounded-md"
            />
            <input
              type="number"
              placeholder="Max"
              value={filters.maxPrice}
              onChange={(e) =>
                setFilters({ ...filters, maxPrice: e.target.value })
              }
              className="w-1/2 p-2 border rounded-md"
            />
          </div>
        </div>

        {/* Sort Options */}
        <div className="mb-6">
          <label className="block font-semibold text-gray-700 mb-2">
            Sort By
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full p-2 border rounded-md"
          >
            <option value="default">Default</option>
            <option value="priceAsc">Price: Low to High</option>
            <option value="priceDesc">Price: High to Low</option>
            <option value="newest">Newest First</option>
            <option value="popular">Most Popular</option>
          </select>
        </div>
      </div>

      {/* Products Grid */}
      <div className="flex-1 p-6">
        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search products..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full p-3 border rounded-lg shadow-sm"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, index) => (
              <ProductCardSkeleton key={index} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart, removeFromCart, cart } = useCartStore();
  const { addToWishlist, removeFromWishlist, wishlist } = useWishlistStore();

  const discountedPrice = product.price * (1 - product.discount / 100);
  const totalStock =
    product.variation[0]?.color.reduce((sum, c) => sum + (Number(c.stock) || 0), 0) || 0;
  const isInCart = cart.some(i => i._id === product._id);
  const isInWishlist = wishlist.some(i => i._id === product._id);
  const isOOS = totalStock === 0;

  const fmt = (n) => `₹ ${n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

  const handleCart = (e) => {
    e.stopPropagation();
    if (isOOS) { toast.error("Out of stock"); return; }
    if (isInCart) {
      removeFromCart(product._id);
      toast.success(`${product.name} removed from cart`);
    } else {
      addToCart(product);
      toast.success(`${product.name} added to cart`);
    }
  };

  const handleWishlist = (e) => {
    e.stopPropagation();
    if (isInWishlist) {
      removeFromWishlist(product._id);
      toast.success(`${product.name} removed from wishlist`);
    } else {
      addToWishlist(product);
      toast.success(`${product.name} added to wishlist`);
    }
  };

  return (
    <>
      <style>{`
        .gtc { position:relative; background:#fff; border:1px solid #e5e7eb; overflow:hidden;
               display:flex; flex-direction:column; cursor:pointer;
               transition:border-color .25s, box-shadow .25s; }
        .gtc:hover { border-color:#0d9488; box-shadow:0 8px 32px rgba(13,148,136,.12); }

        /* image */
        .gtc__img-wrap { position:relative; overflow:hidden; aspect-ratio:4/3; }
        .gtc__img { width:100%; height:100%; object-fit:cover;
                    transition:transform .5s cubic-bezier(.25,.46,.45,.94); }
        .gtc:hover .gtc__img { transform:scale(1.06); }

        /* hover overlay */
        .gtc__overlay {
          position:absolute; inset:0;
          background:linear-gradient(to top, rgba(7,94,84,.82) 0%, rgba(7,94,84,.1) 55%, transparent 100%);
          opacity:0; transition:opacity .28s;
          display:flex; flex-direction:column; align-items:stretch;
          justify-content:flex-end; padding:12px; gap:8px;
        }
        .gtc:hover .gtc__overlay { opacity:1; }

        /* overlay buttons row */
        .gtc__actions { display:flex; gap:8px; }
        .gtc__btn {
          flex:1; display:flex; align-items:center; justify-content:center; gap:6px;
          padding:9px 4px; border:none; cursor:pointer;
          font-size:.73rem; font-weight:700; letter-spacing:.07em;
          text-transform:uppercase; color:#fff;
          backdrop-filter:blur(6px); transition:background .2s;
        }
        .gtc__btn--cart   { background:rgba(255,255,255,.18); }
        .gtc__btn--cart:hover { background:rgba(255,255,255,.28); }
        .gtc__btn--cart.in-cart { background:rgba(220,38,38,.7); }
        .gtc__btn--wish   { flex:0 0 40px; background:rgba(255,255,255,.18); }
        .gtc__btn--wish:hover { background:rgba(255,255,255,.28); }
        .gtc__btn--wish.in-wish { background:rgba(220,38,38,.7); }
        .gtc__btn--view   { flex:0 0 40px; background:rgba(255,255,255,.12); }
        .gtc__btn--view:hover { background:rgba(255,255,255,.22); }

        /* top badges */
        .gtc__badge {
          position:absolute; top:10px; font-size:.62rem; font-weight:800;
          letter-spacing:.1em; text-transform:uppercase; padding:3px 9px; line-height:1.5;
        }
        .gtc__badge--left  { left:0; background:#0d9488; color:#fff; }
        .gtc__badge--red   { right:10px; background:#ef4444; color:#fff; border-radius:3px; }
        .gtc__badge--dark  { right:10px; background:#111; color:#fff; border-radius:3px; }

        /* info */
        .gtc__info { padding:14px 14px 16px; display:flex; flex-direction:column; gap:6px; flex:1; }
        .gtc__cat  { font-size:.6rem; font-weight:700; letter-spacing:.15em; text-transform:uppercase;
                     color:#0d9488; }
        .gtc__name { font-size:.9rem; font-weight:600; color:#111827; line-height:1.3;
                     display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
        .gtc__desc { font-size:.75rem; color:#9ca3af; line-height:1.45;
                     display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }

        /* price row */
        .gtc__price-row { display:flex; align-items:center; gap:8px; margin-top:auto; padding-top:4px;
                          border-top:1px solid #f3f4f6; }
        .gtc__price-now  { font-size:1rem; font-weight:700; color:#0f766e; }
        .gtc__price-was  { font-size:.78rem; color:#c4b5b5; text-decoration:line-through; }
        .gtc__stock      { margin-left:auto; display:flex; align-items:center; gap:4px;
                          font-size:.67rem; font-weight:600; }
        .gtc__stock--in  { color:#10b981; }
        .gtc__stock--out { color:#9ca3af; }
        .gtc__stock-dot  { width:6px; height:6px; border-radius:50%; flex-shrink:0; }
        .gtc__stock-dot--in  { background:#10b981; }
        .gtc__stock-dot--out { background:#d1d5db; }
      `}</style>

      <div className="gtc" onClick={() => navigate(`/product/${product._id}`)}
        role="button" tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && navigate(`/product/${product._id}`)}>

        {/* ── Image ── */}
        <div className="gtc__img-wrap">
          <img src={product.images[0]} alt={product.name} className="gtc__img" loading="lazy" />

          {/* Discount badge */}
          {product.discount > 0 && (
            <span className="gtc__badge gtc__badge--left">−{product.discount}%</span>
          )}

          {/* Right badge: Sold Out > Hot > nothing */}
          {isOOS ? (
            <span className="gtc__badge gtc__badge--dark">Sold Out</span>
          ) : product.discount >= 20 ? (
            <span className="gtc__badge gtc__badge--red">
              <FontAwesomeIcon icon={faFire} className="mr-1" style={{ fontSize: '.6rem' }} />Hot
            </span>
          ) : null}

          {/* Hover overlay */}
          <div className="gtc__overlay">
            <div className="gtc__actions">
              {/* Cart */}
              <button
                className={`gtc__btn gtc__btn--cart ${isInCart ? 'in-cart' : ''}`}
                onClick={handleCart}
                aria-label={isInCart ? 'Remove from cart' : 'Add to cart'}
              >
                <FontAwesomeIcon icon={isOOS ? faBan : isInCart ? faTrashAlt : faCartShopping}
                  style={{ fontSize: '.78rem' }} />
                {isOOS ? 'Out of Stock' : isInCart ? 'Remove' : 'Add to Cart'}
              </button>

              {/* Wishlist */}
              <button
                className={`gtc__btn gtc__btn--wish ${isInWishlist ? 'in-wish' : ''}`}
                onClick={handleWishlist}
                aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                <FontAwesomeIcon icon={isInWishlist ? faTrashAlt : faHeart}
                  style={{ fontSize: '.82rem' }} />
              </button>

              {/* Quick view */}
              <button
                className="gtc__btn gtc__btn--view"
                onClick={e => { e.stopPropagation(); navigate(`/product/${product._id}`); }}
                aria-label="View product"
              >
                <FontAwesomeIcon icon={faEye} style={{ fontSize: '.82rem' }} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Info ── */}
        <div className="gtc__info">
          <p className="gtc__cat">{product.category?.name}</p>
          <h3 className="gtc__name">{product.name}</h3>
          <p className="gtc__desc">{product.description}</p>

          <div className="gtc__price-row">
            <span className="gtc__price-now">{fmt(discountedPrice)}</span>
            {product.discount > 0 && (
              <span className="gtc__price-was">{fmt(product.price)}</span>
            )}
            <span className={`gtc__stock ${isOOS ? 'gtc__stock--out' : 'gtc__stock--in'}`}>
              <span className={`gtc__stock-dot ${isOOS ? 'gtc__stock-dot--out' : 'gtc__stock-dot--in'}`} />
              {isOOS ? 'Out of stock' : `${totalStock} in stock`}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductPage;
